// components/DataGrid/index.js
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from "react";
import axios from "axios";
import Pagination from "./pagination.js";
import Loading from "../Loading/index.js";

const DataGrid = forwardRef(({
  endpoint,
  columns,
  filters: externalFilters = {},
  onRowClick,
  emptyMessage = "Nenhum registro encontrado",
  emptyAction,
  headerAction,
  permitirAlterarQtdPerPage,
  rowKey = "id",
}, ref) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const filterTimer = useRef(null);
  const isFirstRender = useRef(true);
  const prevParamsRef = useRef(null);

  // Memoriza os filtros externos para evitar loops
  const filtersKey = JSON.stringify(externalFilters);
  const memoizedFilters = useMemo(
    () => externalFilters,
    [filtersKey]
  );

  // Função de fetch que pode ser chamada externamente
  const doFetch = async () => {
    setLoading(true);
    try {
      const params = {
        filtro: debouncedFilter,
        page: currentPage,
        perPage,
        sortField,
        sortDirection,
        ...memoizedFilters
      };

      const res = await axios.get(endpoint, { params });
      setData(res.data.data.items);
      setTotal(res.data.data.total);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  // Expõe refresh para o componente pai
  useImperativeHandle(ref, () => ({
    refresh: doFetch
  }));

  // Debounce do filtro
  useEffect(() => {
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => {
      setDebouncedFilter(filterText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(filterTimer.current);
  }, [filterText]);

  // Fetch quando mudar paginação, filtro ou ordenação
  useEffect(() => {
    const currentParamsKey = JSON.stringify({
      endpoint,
      currentPage,
      perPage,
      debouncedFilter,
      sortField,
      sortDirection,
      filters: memoizedFilters
    });

    // Evita chamada duplicada se os params não mudaram
    if (prevParamsRef.current === currentParamsKey && !isFirstRender.current) {
      return;
    }

    prevParamsRef.current = currentParamsKey;
    isFirstRender.current = false;

    doFetch();
  }, [endpoint, currentPage, perPage, debouncedFilter, sortField, sortDirection, memoizedFilters]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header com filtro e ação */}
      <div className="p-4 border-b flex justify-between items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            placeholder="Buscar..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        {headerAction}
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.field || col.key}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    col.sortable ? "cursor-pointer select-none hover:bg-gray-100" : ""
                  } ${col.headerClassName || ""}`}
                  style={{ width: col.width }}
                  onClick={() => col.sortable && handleSort(col.field)}
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortable && sortField === col.field && (
                      <span className="text-slate-900">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns?.length} className="px-4 py-8 text-center text-gray-500">
                  <div className="flex justify-center items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    <Loading />
                  </div>
                </td>
              </tr>
            ) : data?.length === 0 ? (
              <tr>
                <td colSpan={columns?.length} className="px-4 py-8 text-center">
                  <p className="text-gray-500 mb-4">{emptyMessage}</p>
                  {emptyAction}
                </td>
              </tr>
            ) : (
              data?.map((row) => (
                <tr
                  key={row[rowKey]}
                  className={`hover:bg-gray-50 ${onRowClick ? "cursor-pointer" : ""}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns?.map((col) => (
                    <td
                      key={col.field || col.key}
                      className={`px-4 py-3 text-sm ${col.className || ""}`}
                    >
                      {col.render ? col.render(row) : row[col.field]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {!loading && data?.length > 0 && (
        <Pagination
          permitirAlterarQtdPerPage={permitirAlterarQtdPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setCurrentPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setCurrentPage(1);
          }}
        />
      )}
    </div>
  );
});

export default DataGrid;